import React, { useEffect, useState } from "react";
import { TextField, FormValidationSummary, ValidationResult, FailureSeverity } from "sprint-common-web";

import { CrossTypeContentSearchItem } from "../../../../../common/components/form/CrossTypeContentSearch";
import { ContentReferenceType } from "../../../../../common/types/ContentReferenceType";
import { ensureIsAncillaryDetails } from "../../../../../content-ingestion/cloud-files/details/sections/associate/context/CloudFileAssociationContentDetails";
import { ensureAncillaryDetails } from "../../../../../content-ingestion/common/content/types/ContentAssociationDetails";
import { useCreateOrder } from "../../common/provider/useCreateOrder";

export const OrderAncillaryVersionSelector = (): JSX.Element => {
    const { contentDetails, setAncillaryVersion, airline, setHasAirlineMismatch } = useCreateOrder();
    const [ancillaryAirlineValidationResult, setAncillaryAirlineValidationResult] = useState<ValidationResult>();

    useEffect(() => {
        if (!airline || !contentDetails) {
            setAncillaryVersion(undefined);
            setAncillaryAirlineValidationResult(undefined);
            setHasAirlineMismatch(false);

            return;
        }

        ensureIsAncillaryDetails(contentDetails);

        const doesAncillaryContainAirline =
            airline && contentDetails.version.airlines.some((item) => item.id === airline.id);

        if (!doesAncillaryContainAirline) {
            const supportedAirlines = contentDetails.version.airlines.map((a) => a.name).join(", ");

            setHasAirlineMismatch(true);
            setAncillaryAirlineValidationResult({
                isValid: false,
                failures: [
                    {
                        code: "ANCILLARY_AIRLINE_MISMATCH",
                        message: `The selected ancillary does not include the airline ${airline.name}. Please select a different ancillary or change the airline in the Details section to one of the supported airlines: ${supportedAirlines}.`,
                        severity: FailureSeverity.Error
                    }
                ]
            });

            return;
        }

        setHasAirlineMismatch(false);
        setAncillaryAirlineValidationResult(undefined);
        setAncillaryVersion({
            type: ContentReferenceType.Ancillary,
            subtype: contentDetails.subtype,
            ancillary: {
                id: contentDetails.id,
                title: contentDetails.title,
                buzzId: contentDetails.buzz?.id
            },
            version: {
                id: contentDetails.version.id,
                buzzId: contentDetails.version.buzzId,
                type: contentDetails.version.type,
                airlines: contentDetails.version.airlines,
                distributor: contentDetails.version.distributor
            }
        });
    }, [airline, contentDetails]);

    const ancillaryVersion = getAncillaryVersion({ contentDetails });

    return (
        <>
            <TextField
                label="Version*"
                placeholder="Search and select ancillary to set version"
                value={ancillaryVersion}
                contentEditable={false}
                disabled={true}
                name="contentVersionId"
                data-test-id="create-order-ancillary-version"
            />
            {ancillaryAirlineValidationResult && (
                <FormValidationSummary
                    validationResult={ancillaryAirlineValidationResult}
                    styles={{ root: { margin: "10px 0" } }}
                />
            )}
        </>
    );
};

const getAncillaryVersion = ({ contentDetails }: { contentDetails?: CrossTypeContentSearchItem }) => {
    if (!contentDetails) {
        return;
    }

    ensureAncillaryDetails(contentDetails);

    return contentDetails.version.type.name;
};
